<script lang="ts" setup>
import { Button } from '@/components/ui/button';
import { LineChart } from '@/components/ui/chart-line';
import RangeCalendarEditor from '@/ui-plus/date-time-prop-editor/RangeCalendarEditor.vue';
import {
    type DateRangeValueType,
} from "@lib/zod/dateTimePropEditor";
import { storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
import { toast } from "@iframe-widgets/pages/widgets/toast/iframeToast";
import { computed, onMounted, ref, watch } from 'vue';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    Banknote,
    Loader2,
    CircleOff,
    ShoppingCart,
    TrendingUp,
    // DollarSign, Activity, CreditCard, Users
} from 'lucide-vue-next';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Switch } from '@/components/ui/switch';
import { formatCurrency } from '@lib/utils/formatCurrency';
// import { getOrderPlatformSupportFeeParts } from '@lib/utils/orderPlatformSupportFeeParts';
import OrdersList from '@orders-spa-vue/pages/OrdersList.vue';
import { formatChartBreakdown, type BreakdownData } from './printChartBreakdown';
import { zmgPublicCacheClient } from '@api-client/zmg_cache';

const ApiPath = storeOrdersApiClient[":storeId"].get_by_order_ids.$post;
type OrdersData = ResType<typeof ApiPath>["data"];
const ordersData = ref<OrdersData>([]);

const props = defineProps<{
    currentStoreId: string;
    cachedStoreOrders: Record<string, OrdersData[number]>;
    isPrintBreakdownBtnVisible?: boolean;
}>();

const storeCachedStoreOrders = ref<Record<string, OrdersData[number]>>({});
const storeCachedStoreRangeFilteredOrders = ref<Record<string, OrdersData[number]>>({});
Object.values(props.cachedStoreOrders).forEach(order => {
    if (order.storeOrderId && order.storeId === props.currentStoreId) {
        storeCachedStoreOrders.value[order.storeOrderId] = order;
    }
});

type TaxSettings = ResType<typeof zmgPublicCacheClient.store.tax_settings[':id']['data.json']['$get']>["data"];
const taxSettings = ref<TaxSettings>();


const ApiPathRecentOrders = storeOrdersApiClient[":storeId"].recent_orders_report.$post;
type RecentOrdersType = ResType<typeof ApiPathRecentOrders>["data"];
const orderIds = ref<RecentOrdersType>([]);

const loadTaxSettings = async () => {
    const { data, error } = await (await zmgPublicCacheClient.store.tax_settings[':id']['data.json'].$get({
        param: {
            id: props.currentStoreId,
        }
    })).json();
    if (error) {
        toast.error('Failed to load tax settings');
        console.error(error);
    }
    if (data) {
        taxSettings.value = data;
    }
}

const dateLabel = ref('Today');
const today = new Date();

const todayStarting = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);

const dateRange = ref<DateRangeValueType>({
    start: todayStarting.getTime(),
    end: Date.now(),
});



const paymentMethodNames_OrderType: Record<string, number> = {};

let groupedData: Record<string, {
    date: string,
    count: number,
    total: number,
    deliveryFees: number,
    paymentMethods: Record<string, number>,
    // deliveryOrders: number,
    // pickupOrders: number
}> = {};

const result = ref<{
    date: string,
    count: number,
    total: number,
    deliveryFees: number,
    [paymentMethodName: string]: string | number,
}[]>([]);

const isLoading = ref(false);
const loadData = async () => {
    isLoading.value = true;
    isOrdersListVisible.value = false;
    isChartVisible.value = false;
    // await new Promise(resolve => setTimeout(resolve, 2000));
    const { data, error } = await (await storeOrdersApiClient[':storeId'].recent_orders_report.$post({
        param: {
            storeId: props.currentStoreId,
        },
        json: {
            start: dateRange.value.start,
            end: dateRange.value.end,
        }
    })).json();
    isLoading.value = false;

    if (error) {
        toast.error('Failed to load analytics data');
        console.error(error);
    }
    orderIds.value = data || [];
    // load orders data if it is not in cachedStoreOrders
    const newOrderIds = orderIds.value.filter(order => !storeCachedStoreOrders.value[order.storeOrderId]);
    if (newOrderIds.length > 0) {
        const { data, error } = await (await storeOrdersApiClient[':storeId'].get_by_order_ids.$post({
            param: { storeId: props.currentStoreId },
            json: { orderIds: newOrderIds.map(order => order.storeOrderId) }
        })).json();
        if (error) {
            toast.error('Failed to load orders data');
            console.error(error);
        }
        if (data) {
            data.forEach(order => {
                if (!storeCachedStoreOrders.value[order.storeOrderId]) {
                    storeCachedStoreOrders.value[order.storeOrderId] = order;
                }
                props.cachedStoreOrders[order.storeOrderId] = order;
            });
        }
    }
    storeCachedStoreRangeFilteredOrders.value = {};
    Object.values(storeCachedStoreOrders.value).forEach(order => {
        if (order.order_placed_at_ts >= (dateRange.value.start ?? 0) &&
            order.order_placed_at_ts <= (dateRange.value.end ?? Date.now())) {
            storeCachedStoreRangeFilteredOrders.value[order.storeOrderId] = order;
        }
    });
    ordersData.value = [];
    Object.values(storeCachedStoreRangeFilteredOrders.value).forEach(order => {
        ordersData.value.push(order);
    });
    ordersData.value.sort((a, b) => a.order_placed_at_ts - b.order_placed_at_ts);

    groupedData = {};
    // add all dates between start and end to groupedData
    const startDate = new Date(dateRange.value.start ?? 0);
    const endDate = new Date(dateRange.value.end ?? 0);
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
        const groupKey = currentDate.toLocaleDateString('en-CA');
        groupedData[groupKey] = {
            date: groupKey,
            count: 0,
            total: 0,
            deliveryFees: 0,
            paymentMethods: {},
        };
        currentDate.setDate(currentDate.getDate() + 1);
    }

    ordersData.value.forEach(order => {
        if (order.isCancelled) {
            return;
        }
        // order.subtotalJSON.bestDeliveryZone.totalWithTax
        const date = new Date(order.order_placed_at_ts);
        // Convert to store's timezone and get the date part
        // 'en-CA' gives YYYY-MM-DD format
        const groupKey = date.toLocaleDateString('en-CA');
        if (!groupedData[groupKey]) {
            groupedData[groupKey] = {
                date: groupKey,
                count: 0,
                total: 0,
                deliveryFees: 0,
                paymentMethods: {},
            };
        }

        groupedData[groupKey].count += 1;
        groupedData[groupKey].total += order.finalAmount;

        // Track delivery fees
        if (order.subtotalJSON?.bestDeliveryZone?.totalWithTax) {
            groupedData[groupKey].deliveryFees += order.subtotalJSON.bestDeliveryZone.totalWithTax;
        }

        // Track payment methods per day
        if (order.paymentMethodJSON?.name) {
            const paymentMethodName = order.paymentMethodJSON.name;
            const orderType = order.orderType;
            const key = `${paymentMethodName}-${orderType}`;
            groupedData[groupKey].paymentMethods[key] =
                (groupedData[groupKey].paymentMethods[key] || 0) + order.finalAmount;

            paymentMethodNames_OrderType[key] = (paymentMethodNames_OrderType[key] || 0) + order.finalAmount;
        }
    });
    // Convert to array and sort by date
    result.value = Object.values(groupedData).map((item) => {
        const obj = {
            date: item.date,
            count: item.count,
            total: Number(item.total.toFixed(2)),
            deliveryFees: Number(item.deliveryFees.toFixed(2)),
            ...Object.fromEntries(
                Object.entries(item.paymentMethods).map(([method, amount]) => [
                    method,
                    Number(amount.toFixed(2))
                ])
            ),
        }
        // add any missing key as 0
        Object.keys(paymentMethodNames_OrderType).forEach(key => {
            if (!(obj as any)[key]) {
                (obj as any)[key] = 0;
            }
        });
        return obj;
    }).sort((a, b) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
    );
};


const setDateRange = (days: number) => {
    dateLabel.value = `Last ${days} days`;
    isCustomDateRange.value = false;
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - days);

    // Get opening time for the start date
    // const openingTime = getOpeningTimeForDay(startDate);
    // startDate.setHours(openingTime.hour, openingTime.minute, 0, 0);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    // Get closing time for today
    // const closingTime = getClosingTimeForDay(endDate);
    // endDate.setHours(closingTime.hour, closingTime.minute, 59, 999);
    endDate.setHours(23, 59, 59, 999);

    dateRange.value = {
        start: startDate.getTime(),
        end: endDate.getTime(),
    };
};

const setThisWeek = () => {
    dateLabel.value = 'This week';
    isCustomDateRange.value = false;
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Go to Sunday

    // Get opening time for the start of week
    // const openingTime = getOpeningTimeForDay(startOfWeek);
    // startOfWeek.setHours(openingTime.hour, openingTime.minute, 0, 0);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Go to Saturday

    // Get closing time for the end of week
    // const closingTime = getClosingTimeForDay(endOfWeek);
    // endOfWeek.setHours(closingTime.hour, closingTime.minute, 59, 999);
    endOfWeek.setHours(23, 59, 59, 999);

    dateRange.value = {
        start: startOfWeek.getTime(),
        end: endOfWeek.getTime(),
    };
};

const setThisMonth = () => {
    dateLabel.value = 'This month';
    isCustomDateRange.value = false;
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    // Get opening time for the first day of month
    // const openingTime = getOpeningTimeForDay(startOfMonth);
    // startOfMonth.setHours(openingTime.hour, openingTime.minute, 0, 0);
    startOfMonth.setHours(0, 0, 0, 0);

    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    // Get closing time for the last day of month
    // const closingTime = getClosingTimeForDay(endOfMonth);
    // endOfMonth.setHours(closingTime.hour, closingTime.minute, 59, 999);
    endOfMonth.setHours(23, 59, 59, 999);

    dateRange.value = {
        start: startOfMonth.getTime(),
        end: endOfMonth.getTime(),
    };
};

let ignoreFirstLoad = true;
// Watch for date range changes and reload data
let debounceTimer: ReturnType<typeof setTimeout> | null = null
watch(dateRange, () => {
    if (debounceTimer) {
        clearTimeout(debounceTimer)
    }
    debounceTimer = setTimeout(() => {
        if (!ignoreFirstLoad) {
            loadData();
        }
        ignoreFirstLoad = false;
    }, 1000)
}, { deep: true, immediate: false });


onMounted(async () => {
    // const { data, error } = await (await dashboardApiClient.store[':storeId'].open_hours.get.$post({
    //     param: {
    //         storeId: props.currentStoreId,
    //     },
    //     json: {
    //         columns: {
    //             timezone: true,
    //             defaultOpenHours: true,
    //         }
    //     }
    // })).json();
    // if (error) {
    //     toast.error('Failed to load open hours');
    //     console.error(error);
    // }
    // if (data) {
    //     console.log(data);
    //     openHoursData.value = data; // Store the open hours data
    // }
    const todayStarting = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    // const todayStartingOpeningTime = getOpeningTimeForDay(todayStarting);
    // todayStarting.setHours(todayStartingOpeningTime.hour, todayStartingOpeningTime.minute, 0, 0);
    todayStarting.setHours(0, 0, 0, 0);

    const endToday = new Date();
    // const closingTime = getClosingTimeForDay(endToday);  
    // endToday.setHours(closingTime.hour, closingTime.minute, 59, 999);
    endToday.setHours(23, 59, 59, 999);

    dateRange.value = {
        start: todayStarting.getTime() - (import.meta.env.DEV ? (1000 * 60 * 60 * 24 * 7) : 0),
        end: endToday.getTime(),
    };
    await loadTaxSettings();
    await loadData();
});


const isCustomDateRange = ref(false);
const totalRevenue = computed(() => {
    return formatCurrency(ordersData.value.reduce((acc, order) => {
        if (order.isCancelled) {
            return acc;
        }
        return acc + order.finalAmount;
    }, 0), taxSettings.value?.currency);
});

const cancelledRevenue = computed(() => {
    return formatCurrency(ordersData.value.reduce((acc, order) => {
        if (order.isCancelled) {
            return acc + order.finalAmount;
        }
        return acc;
    }, 0), taxSettings.value?.currency);
});

const paymentBreakdown = computed(() => {
    const breakdown: Record<string, { total: number, count: number }> = {};

    ordersData.value.forEach(order => {
        if (order.isCancelled) return;

        if (order.paymentMethodJSON?.name) {
            const key = `${order.paymentMethodJSON.name}-${order.orderType}`;
            if (!breakdown[key]) {
                breakdown[key] = { total: 0, count: 0 };
            }
            breakdown[key].total += order.finalAmount;
            breakdown[key].count += 1;
        }
    });

    return Object.entries(breakdown)
        .map(([key, data]) => ({
            method: key,
            total: data.total,
            count: data.count,
            formattedTotal: formatCurrency(data.total, taxSettings.value?.currency)
        }))
        .sort((a, b) => b.total - a.total);
});

const grandTotal = computed(() => {
    const total = paymentBreakdown.value.reduce((acc, item) => acc + item.total, 0);
    const count = paymentBreakdown.value.reduce((acc, item) => acc + item.count, 0);
    return {
        total,
        count,
        formattedTotal: formatCurrency(total, taxSettings.value?.currency)
    };
});

const totalDeliveryFees = computed(() => {
    return formatCurrency(ordersData.value.reduce((acc, order) => {
        if (order.isCancelled) {
            return acc;
        }
        return acc + (order.subtotalJSON?.bestDeliveryZone?.totalWithTax || 0);
    }, 0), taxSettings.value?.currency);
});

// const platformSupportFeeBreakdown = computed(() => {
//     const totals = {
//         customerPaid: 0,
//         storeCovered: 0,
//     };

//     ordersData.value.forEach((order) => {
//         if (order.isFeeRefunded) return;
//         const { customerPaid, storeCovered } = getOrderPlatformSupportFeeParts(
//             order.cartBreakdownJSON,
//             order.taxSettingsJSON,
//         );
//         totals.customerPaid += customerPaid;
//         totals.storeCovered += storeCovered;
//     });

//     const currency = taxSettings.value?.currency;
//     const rows = [
//         {
//             label: 'Platform Support Fee',
//             info: 'Paid by customer as an extra',
//             total: totals.customerPaid,
//             formattedTotal: formatCurrency(totals.customerPaid, currency),
//         },
//         // {
//         //     label: 'Platform Support Fee',
//         //     info: 'Paid by seller',
//         //     total: totals.storeCovered,
//         //     formattedTotal: formatCurrency(totals.storeCovered, currency),
//         // },
//     ].filter((row) => row.total > 0);

//     return rows;
// });

const platformSupportFeeBreakdown = ref<{ label: string, info: string, total: number, formattedTotal: string }[]>([]);

const averageOrderValue = computed(() => {
    const nonCancelledOrders = ordersData.value.filter(order => !order.isCancelled);
    if (nonCancelledOrders.length === 0) return formatCurrency(0, taxSettings.value?.currency);

    const totalRevenue = nonCancelledOrders.reduce((acc, order) => acc + order.finalAmount, 0);
    const avgValue = totalRevenue / nonCancelledOrders.length;

    return formatCurrency(avgValue, taxSettings.value?.currency);
});

const countDeliveries = computed(() => {
    return ordersData.value.filter(order => !order.isCancelled && order.orderType === 'delivery').length;
});

const isOrdersListVisible = ref(false);
const isChartVisible = ref(true);
const getFormattedRange = () => {
    return `${new Date(dateRange.value.start ?? 0).toLocaleDateString('en-CA')} [${new Date(dateRange.value.start ?? 0).toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit',
        hour12: false
    })}] - ${new Date(dateRange.value.end ?? 0).toLocaleDateString('en-CA')} [${new Date(dateRange.value.end ?? 0).toLocaleTimeString([], {
        hour: '2-digit', minute: '2-digit',
        hour12: false
    })}]`;
}

const emit = defineEmits<{
    (e: 'printBreakdown', html: string): void
}>();

const handlePrintBreakdown = () => {
    const breakdownData: BreakdownData = {
        paymentBreakdown: paymentBreakdown.value,
        grandTotal: grandTotal.value,
        totalDeliveryFees: totalDeliveryFees.value,
        countDeliveries: countDeliveries.value,
        platformSupportFeeBreakdown: platformSupportFeeBreakdown.value,
        dateRange: dateRange.value,
        currency: taxSettings.value?.currency || 'GBP'
    };

    // printChartBreakdown(breakdownData);
    const html = formatChartBreakdown(breakdownData);
    //emit html to parent
    emit('printBreakdown', html);
}
</script>

<template>
    <div class="space-y-6">

        <DropdownMenu>
            <DropdownMenuTrigger as-child>
                <Button variant="outline"
                        size="sm"
                        class="mt-4">
                    {{ isCustomDateRange ? 'Custom date' : dateLabel }}
                    {{ getFormattedRange() }}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                <!-- Date Range Preset Buttons -->
                <DropdownMenuItem @click="setDateRange(0)">
                    Today
                </DropdownMenuItem>
                <DropdownMenuItem @click="setDateRange(1)">
                    Last 2 days
                </DropdownMenuItem>
                <DropdownMenuItem @click="setDateRange(3)">
                    Last 3 days
                </DropdownMenuItem>
                <DropdownMenuItem @click="setDateRange(7)">
                    Last 7 days
                </DropdownMenuItem>
                <DropdownMenuItem @click="setDateRange(30)">
                    Last 30 days
                </DropdownMenuItem>
                <DropdownMenuItem @click="setDateRange(90)">
                    Last 3 months
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem @click="setThisWeek">
                    This week
                </DropdownMenuItem>
                <DropdownMenuItem @click="setThisMonth">
                    This month
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div>
                    <Switch :checked="isCustomDateRange"
                            @update:checked="isCustomDateRange = !isCustomDateRange">
                    </Switch>
                    Custom
                    <RangeCalendarEditor v-model="dateRange"
                                         v-if="isCustomDateRange" />
                </div>
            </DropdownMenuContent>
        </DropdownMenu>

        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mt-2 max-w-full">
            <Card>
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        Total Revenue
                    </CardTitle>
                    <Banknote />
                </CardHeader>
                <CardContent>
                    <Loader2 v-if="isLoading"
                             class="animate-spin min-w-8 min-h-8" />
                    <div v-else
                         class="text-2xl font-bold">
                        {{ totalRevenue }}
                    </div>
                    <p class="text-xs text-muted-foreground">
                        {{ grandTotal.count }} non-cancelled orders
                    </p>
                </CardContent>
            </Card>
            <!-- <Card>
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        Total Delivery Fees
                    </CardTitle>
                    <TrendingUp />
                </CardHeader>
                <CardContent>
                    <Loader2 v-if="isLoading"
                             class="animate-spin min-w-8 min-h-8" />
                    <div v-else
                         class="text-2xl font-bold">
                        {{ totalDeliveryFees }}
                    </div>
                </CardContent>
            </Card> -->
            <Card>
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        Cancelled Orders
                    </CardTitle>
                    <CircleOff />
                </CardHeader>
                <CardContent>
                    <div class="text-2xl font-bold">
                        {{ cancelledRevenue }}
                    </div>
                    <p class="text-xs text-muted-foreground">
                        {{ordersData.filter(order => order.isCancelled).length}} orders cancelled
                    </p>
                </CardContent>
            </Card>

            <Card>
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        Total Orders
                    </CardTitle>
                    <ShoppingCart />
                </CardHeader>
                <CardContent>
                    <div class="text-2xl font-bold">
                        {{ ordersData.length }}
                    </div>
                    <!-- <p class="text-xs text-muted-foreground">
                        +19% from last month
                    </p> -->
                </CardContent>
            </Card>
            <Card>
                <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle class="text-sm font-medium">
                        Average Order Value
                    </CardTitle>
                    <TrendingUp />
                </CardHeader>
                <CardContent>
                    <Loader2 v-if="isLoading"
                             class="animate-spin min-w-8 min-h-8" />
                    <div v-else
                         class="text-2xl font-bold">
                        {{ averageOrderValue }}
                    </div>
                    <!-- <p class="text-xs text-muted-foreground">
                        +12% from last month
                    </p> -->
                </CardContent>
            </Card>
        </div>

        <!-- <div class="grid gap-4 md:grid-cols-1 lg:grid-cols-1 mt-2"></div> -->

        <Card>
            <CardHeader>
                <CardTitle class="text-lg font-medium">
                    Breakdown {{ getFormattedRange() }}
                    <Button @click="handlePrintBreakdown"
                            v-if="isPrintBreakdownBtnVisible"
                            variant="outline"
                            size="sm"
                            :disabled="isLoading">
                        Print
                    </Button>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <Loader2 v-if="isLoading"
                         class="animate-spin min-w-8 min-h-8" />
                <Table v-else>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Payment Method - Order Type</TableHead>
                            <TableHead class="text-right">Amount</TableHead>
                            <TableHead class="text-right">Count</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow v-for="item in paymentBreakdown"
                                  :key="item.method">
                            <TableCell class="font-medium">{{ item.method }}</TableCell>
                            <TableCell class="text-right">{{ item.formattedTotal }}</TableCell>
                            <TableCell class="text-right">{{ item.count }}</TableCell>
                        </TableRow>
                        <TableRow class="font-bold border-t-2">
                            <TableCell class="font-bold">Total</TableCell>
                            <TableCell class="text-right font-bold">{{ grandTotal.formattedTotal }}</TableCell>
                            <TableCell class="text-right font-bold">{{ grandTotal.count }}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                        <TableRow class="bg-muted/50">
                            <TableCell class="font-medium">Delivery Fees</TableCell>
                            <TableCell class="text-right">{{ totalDeliveryFees }}</TableCell>
                            <TableCell class="text-right">{{ countDeliveries }}</TableCell>
                        </TableRow>

                        <TableRow v-for="item in platformSupportFeeBreakdown"
                                  :key="item.label"
                                  class="bg-muted/50">
                            <TableCell class="font-medium">
                                {{ item.label }}
                                <span class="text-xs text-muted-foreground block">
                                    {{ item.info }}
                                </span>
                            </TableCell>
                            <TableCell class="text-right">{{ item.formattedTotal }}</TableCell>
                            <TableCell class="text-right"></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </CardContent>
        </Card>


        <Button @click="isChartVisible = !isChartVisible"
                v-if="result.length > 1"
                variant="outline">
            {{ isChartVisible ? 'Hide Chart' : 'Show Chart' }}
        </Button>

        <Card class="py-6"
              v-if="isChartVisible">
            <LineChart v-if="!isLoading"
                       :data="result"
                       index="date"
                       :categories="['count', 'total', 'deliveryFees', ...Object.keys(paymentMethodNames_OrderType)]" />
        </Card>

        <Button @click="isOrdersListVisible = !isOrdersListVisible"
                variant="outline">
            {{ isOrdersListVisible ? 'Hide List' : 'Show List' }}
            ({{ ordersData.length }})
        </Button>

        <div class="overflow-y-auto max-h-[500px]"
             v-if="isOrdersListVisible">
            <OrdersList :cachedStoreOrders="storeCachedStoreRangeFilteredOrders" />
        </div>

    </div>
</template>
